<template>
    <Head :title="connectionFullName" />
    <BaseLayout gap="md" no-top-padding content-class="pt-6 md:pt-10" fill-height class="text-pretty">
        <div class="flex items-start gap-4 text-ink md:pr-10">
            <BaseLink :href="connections_page_url" :aria-label="t('personDetail.back')" class="mt-2 w-6 shrink-0 text-ink-muted hover:text-ink">
                <i class="bi bi-chevron-left" aria-hidden="true" />
            </BaseLink>
            <div class="min-w-0 grow">
                <h1 class="display-serif fs-mask" translate="no">{{ connectionFullName }}</h1>
                <p v-if="personSubtitle" class="body-small text-ink-muted mt-2">{{ personSubtitle }}</p>
            </div>
            <BaseDropdownMenu :sections="personActions" :aria-label="t('personDetail.actions')" @select="handlePersonAction">
                <template #trigger>
                    <!-- Square at the height of a text button (padding, line height and the 1px border), matching the team page's Manage button. -->
                    <BaseButton
                        ref="personActionsButton"
                        theme="neutral"
                        icon-only
                        class="size-[calc(2*var(--spacing-button-y)+var(--text-button)*var(--leading-button)+2px)]"
                        :aria-label="t('personDetail.actions')"
                        :disabled="!isReady">
                        <i class="bi bi-three-dots" aria-hidden="true" />
                    </BaseButton>
                </template>
            </BaseDropdownMenu>
        </div>
        <BaseTabs v-model="selectedTab" :tab-ids="personTabIds" :labels="personTabLabels" :unmount-on-hide="false" class="md:mx-10" content-class="pt-6">
            <template #content-dynamic>
                <PairPerspective
                    v-if="pair_perspective_url && public_profile_user_id"
                    :key="pair_perspective_url + public_profile_user_id"
                    :url="pair_perspective_url"
                    :connection-id="connection.id"
                    :is-starting-chat="isStartingPairChat"
                    :chat-error="pairChatError"
                    @discuss="handlePairDiscussion" />
            </template>
            <template #content-about>
                <div class="flex flex-col gap-6">
                    <div v-if="hris_data" class="flex items-center gap-2">
                        <SourceChip source="neutral" size="xs"> {{ t("connections.detail.syncedFromHr") }} </SourceChip>
                        <StatusChip v-if="hris_data?.is_inactive" state="neutral" size="xs">{{ t("connections.detail.inactive") }}</StatusChip>
                    </div>
                    <div
                        v-if="public_profile_user_id"
                        class="rounded-lgr focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
                        data-testid="linked-profile-section"
                        tabindex="-1">
                        <PublicProfileSection :target-user-id="public_profile_user_id" :public-profile-enabled="!!public_profile_enabled" />
                    </div>
                    <div v-else-if="!public_profile_enabled" class="flex min-h-106 flex-col items-center justify-center rounded-lg border border-border bg-surface p-6 text-center">
                        <p class="body-serif text-ink">{{ t("connections.detail.profilesOffTitle") }}</p>
                        <p class="body-small mt-1 text-ink-muted">{{ t("connections.detail.profilesOffBody") }}</p>
                    </div>
                    <template v-else>
                        <div class="flex flex-col gap-4">
                            <BaseButton
                                v-if="public_profile_enabled && !showLinkProfileSearch"
                                ref="linkProfileButton"
                                theme="neutral"
                                size="sm"
                                class="flex items-center gap-2 w-fit"
                                @click="handleLinkProfile">
                                <i class="bi bi-plus-lg text-base/none" aria-hidden="true"></i>
                                {{ t("connections.detail.linkProfile") }}
                            </BaseButton>
                            <PublicProfileSearch
                                v-if="public_profile_enabled && showLinkProfileSearch"
                                :connection="connection"
                                :invite-url="collaboration_profile_invite_url"
                                :auto-focus="focusLinkProfileSearch"
                                @dismiss="handleDismissProfileSearch" />
                        </div>
                    </template>

                    <PersonTeams
                        v-if="person_teams_url"
                        :key="person_teams_url"
                        :source="TEAM_INTERACTION_SOURCE.PERSON_DETAIL"
                        :url="person_teams_url"
                        :team-url-template="team_url_template" />
                </div>
            </template>
            <template #content-notes>
                <div class="flex flex-col gap-6">
                    <div v-if="connectionSummary" class="overflow-hidden rounded-2xl border border-border bg-surface-raised">
                        <h3 class="body-serif border-b border-border p-4 text-ink">{{ t("connections.detail.summaryHeading") }}</h3>
                        <MarkdownText :text="connectionSummary" class="body-small p-4 text-ink" />
                    </div>
                    <div v-if="allMemoryItems.length > 0" class="overflow-hidden rounded-2xl border border-border bg-surface-raised">
                        <div class="flex items-center justify-between gap-4 border-b border-border p-4">
                            <h3 class="body-serif text-ink">{{ t("connections.detail.memory.heading") }}</h3>
                            <BaseButton theme="neutral" size="sm" class="flex items-center gap-2" @click="handleNoteAdd">
                                <i class="bi bi-plus-lg text-base/none" aria-hidden="true"></i>
                                {{ t("connections.detail.memory.addNotes") }}
                            </BaseButton>
                        </div>
                        <div class="[&>*:last-child]:border-b-0">
                            <ConnectionContextRow v-for="item in allMemoryItems" :key="item.id" :item="item" />
                        </div>
                    </div>
                    <div v-else class="flex min-h-106 flex-col items-center justify-center rounded-lg border border-border bg-surface p-6 text-center">
                        <p class="body-serif text-ink">{{ t("connections.detail.memory.emptyTitle") }}</p>
                        <p class="body-small mt-1 text-ink-muted">{{ t("connections.detail.memory.emptyHint") }}</p>
                        <BaseButton theme="neutral" size="sm" class="mt-4 flex items-center gap-2" @click="handleNoteAdd">
                            <i class="bi bi-plus-lg text-base/none" aria-hidden="true"></i>
                            {{ t("connections.detail.memory.addNotes") }}
                        </BaseButton>
                    </div>
                    <p class="scaffold-details-small text-ink-muted">{{ t("personDetail.notesPrivacy") }}</p>
                </div>
            </template>
        </BaseTabs>
        <footer class="sticky z-10 mt-auto pt-6 pb-4 md:bottom-0" :class="keyboardHeight ? 'bottom-0' : 'bottom-(--mobile-nav-total-height)'">
            <!-- Masks only the strip below the input's midline, so content stays visible right up to the input. -->
            <div class="absolute inset-x-0 top-1/2 bottom-0 bg-surface" aria-hidden="true" />
            <ChatMessageControls
                ref="chatMessageControls"
                class="relative"
                :location="CHAT_MESSAGE_CONTROLS_LOCATION.CONNECTION_DETAIL"
                :is-disabled="isTranscribing"
                :is-transcribing="isTranscribing"
                is-paste-disabled
                :document-upload-enabled="!!document_upload_enabled"
                :default-text-input="true"
                :is-sending="isSending"
                :initial-value="composerDraft.text ?? chatComposerText"
                :placeholder="placeholderText"
                :user-message-count="0"
                @text-change="composerDraft.text = $event"
                @text-send="handleTextSend"
                @text-keydown="error = null"
                @recording-complete="handleRecordingComplete" />

            <p v-if="error" role="alert" class="body-small relative mt-2 text-ink">{{ error }}</p>
        </footer>
    </BaseLayout>
    <MergeConnectionsDialog v-model:open="isMergeDialogOpen" :current-connection="connection" :return-focus-to="personActionsButton" @merged="handleMerged" />
    <ConfirmationDialog
        v-model:open="isConfirmDeletePersonDialogOpen"
        :title="t('connections.detail.deleteDialog.title')"
        :text="t('connections.detail.deleteDialog.text')"
        :confirm-label="t('common.delete')"
        :return-focus-to="personActionsButton"
        @confirm="handleDeletePerson" />
    <BaseDialog v-model:open="isConfirmUnlinkProfileDialogOpen" :title="t('connections.detail.unlinkDialog.title')" width="narrow" :return-focus-to="personActionsButton">
        <p class="body-small">{{ t("connections.detail.unlinkDialog.text") }}</p>
        <BaseNotification v-if="showUnlinkProfileError" theme="error" hide-dismiss :text="t('connections.detail.unlinkDialog.error')" />
        <template #footer>
            <BaseButton theme="primary" :disabled="isUnlinkingProfile" class="flex items-center justify-center gap-2 w-full md:w-auto" @click="handleUnlinkProfile">
                <LoadingSpinner v-if="isUnlinkingProfile" class="text-white h-4 w-4" />
                {{ t("connections.detail.unlinkDialog.confirm") }}
            </BaseButton>
        </template>
    </BaseDialog>
</template>

<script lang="ts">
import BaseNotification from "~vue/components/BaseNotification.vue";
import PersonTeams from "~vue/Connections/PersonTeams.vue";
import LeftSidebarLayout from "~vue/layouts/LeftSidebarLayout.vue";
import { TEAM_INTERACTION_SOURCE } from "~vue/Teams/teamInteraction";
export default { layout: LeftSidebarLayout };
</script>

<script setup lang="ts">
import { Head, router, useRemember } from "@inertiajs/vue3";
import { useFetch } from "~vue/api";
import type { ComposerMention } from "~vue/api/generated/chat";
import type { ChatInputFile, GlobalProperties, HrisData } from "~vue/appTypes";
import ChatMessageControls from "~vue/ChatDetail/ChatMessageControls.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseDropdownMenu, { type DropdownMenuItem, type DropdownMenuSection } from "~vue/components/BaseDropdownMenu.vue";
import BaseLayout from "~vue/components/BaseLayout.vue";
import BaseTabs from "~vue/components/BaseTabs.vue";
import type BasicInfoCard from "~vue/components/BasicInfoCard.vue";
import ConfirmationDialog from "~vue/components/ConfirmationDialog.vue";
import PublicProfileSection from "~vue/components/PublicProfileSection.vue";
import SourceChip from "~vue/components/SourceChip.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { useConnectionActions } from "~vue/composables/useConnectionActions";
import { useKeyboard } from "~vue/composables/useKeyboard";
import { usePairPerspectiveDiscussion } from "~vue/composables/usePairPerspectiveDiscussion";
import type { AudioStats } from "~vue/composables/useTranscription";
import { useTranscription } from "~vue/composables/useTranscription";
import ConnectionContextRow, { type TimelineItem } from "~vue/ConnectionDetail/ConnectionContextRow.vue";
import MergeConnectionsDialog from "~vue/Connections/MergeConnectionsDialog.vue";
import PairPerspective from "~vue/Connections/PairPerspective.vue";
import PublicProfileSearch from "~vue/Connections/PublicProfileSearch.vue";
import LoadingSpinner from "~vue/icons/LoadingSpinner.vue";
import MarkdownText from "~vue/MarkdownText.vue";
import BaseLink from "~vue/router/BaseLink.vue";
import { useChatStore } from "~vue/stores/chatStore";
import type { ChatEntranceUseCaseData } from "~vue/types/chat";
import type { ConnectionDict } from "~vue/types/connection";
import AmplitudeEventNames from "~vue/utils/amplitudeEventNames";
import { CHAT_MESSAGE_CONTROLS_LOCATION } from "~vue/utils/constants";
import { logError, logUserInteraction } from "~vue/utils/logUtils";
import { isSessionEndedError } from "~vue/utils/sessionEnded";
import { computed, inject, nextTick, onMounted, onUnmounted, ref, toRef, watch } from "vue";
import { useI18n } from "vue-i18n";

const globalProperties = inject<GlobalProperties>("globalProperties");
const { t } = useI18n();
const { keyboardHeight } = useKeyboard();

const {
    connection,
    timeline = [],
    connections_page_url,
    connection_edit_url,
    event_context,
    document_upload_enabled,
    public_profile_enabled,
    public_profile_user_id,
    public_profile_basic_info,
    person_teams_url,
    team_url_template,
    pair_perspective_url,
    hris_data,
    collaboration_profile_invite_url = "",
} = defineProps<{
    connection: ConnectionDict;
    timeline?: TimelineItem[];
    connections_page_url: string;
    connection_edit_url: string;
    event_context: Record<string, string>;
    document_upload_enabled?: boolean;
    public_profile_enabled?: boolean;
    people_detail_discover_use_cases: { enabled: boolean; cases?: ChatEntranceUseCaseData[] };
    public_profile_user_id?: string | null;
    public_profile_basic_info?: InstanceType<typeof BasicInfoCard>["$props"]["basicInfo"] | null;
    person_teams_url?: string | null;
    team_url_template: string;
    pair_perspective_url?: string | null;
    pair_perspective_page_url?: string | null;
    hris_data?: (HrisData & { is_inactive?: boolean }) | null;
    collaboration_profile_invite_url?: string;
}>();

const { getChatMessage, startChat } = useConnectionActions();

const { discuss: handlePairDiscussion, isStartingChat: isStartingPairChat, chatError: pairChatError } = usePairPerspectiveDiscussion(() => connection);

const chatComposerText = computed(() => getChatMessage(connection.persona?.name));
type ComposerDraft = { text: string | null; files: ChatInputFile[] };
const composerDraft = pair_perspective_url
    ? toRef(useRemember<ComposerDraft>({ text: null, files: [] }, `connection-draft:${connection.id}`))
    : ref<ComposerDraft>({ text: null, files: [] });
if (pair_perspective_url) {
    if (composerDraft.value.files.length) {
        useChatStore.removeAllFiles();
        composerDraft.value.files.forEach((file) => useChatStore.addFile(file));
    }
    watch(
        () => useChatStore.files,
        (files) => {
            composerDraft.value.files = [...files];
        },
        { deep: true },
    );
}

const connectionFullName = computed(() => connection.persona?.name || t("connections.detail.yourConnection"));
const connectionSummary = computed(() => connection.connection?.summary || "");
const placeholderText = computed(() => t("connections.detail.composerPlaceholder", { name: connectionFullName.value }));

const computedBasicInfo = computed(() => {
    if (public_profile_basic_info) {
        return public_profile_basic_info;
    }
    return {
        full_name: connection.persona?.name || "",
        role: connection.connection?.job_title || null,
        team: null,
        level: null,
        organization: null,
    };
});

const hasPairPerspective = computed(() => !!(pair_perspective_url && public_profile_user_id));
const personTabLabels = computed(() => ({
    dynamic: t("personDetail.tabs.dynamic"),
    about: t("personDetail.tabs.about"),
    notes: t("personDetail.tabs.notes"),
}));
const personTabIds = computed(() => (hasPairPerspective.value ? ["dynamic", "about", "notes"] : ["about", "notes"]));
const tabState = toRef(useRemember({ selected: hasPairPerspective.value ? "dynamic" : "about" }, `person-tab:${connection.id}`));
const selectedTab = computed({
    get: () => (personTabIds.value.includes(tabState.value.selected) ? tabState.value.selected : "about"),
    set: (value: string) => {
        tabState.value.selected = value;
    },
});
watch(hasPairPerspective, () => {
    tabState.value.selected = selectedTab.value;
});
const personSubtitle = computed(() => [computedBasicInfo.value.role, computedBasicInfo.value.organization].filter(Boolean).join(" · "));
const isMergeDialogOpen = ref(false);
const personActions = computed<DropdownMenuSection[]>(() => [
    {
        id: "person",
        items: [
            { id: "edit", type: "button", label: t("personDetail.edit"), icon: "bi bi-pencil" },
            ...(public_profile_user_id ? [{ id: "unlink", type: "button", label: t("personDetail.unlink"), icon: "bi bi-link-45deg" } satisfies DropdownMenuItem] : []),
            { id: "merge", type: "button", label: t("personDetail.merge"), icon: "bi bi-arrow-left-right" },
            { id: "delete", type: "button", label: t("personDetail.delete"), icon: "bi bi-trash", theme: "danger" },
        ],
    },
]);
function handlePersonAction(item: DropdownMenuItem) {
    switch (item.id) {
        case "edit":
            handleEdit();
            break;
        case "unlink":
            isConfirmUnlinkProfileDialogOpen.value = true;
            break;
        case "merge":
            isMergeDialogOpen.value = true;
            break;
        case "delete":
            isConfirmDeletePersonDialogOpen.value = true;
            break;
    }
}

const isSending = ref(false);
const error = ref<string | null>(null);
const { isTranscribing, transcribe } = useTranscription({ source: "ConnectionDetail", error });
const isReady = ref(false);
const showLinkProfileSearch = ref(true);
const focusLinkProfileSearch = ref(false);
const linkProfileButton = ref<InstanceType<typeof BaseButton> | null>(null);
const personActionsButton = ref<InstanceType<typeof BaseButton> | null>(null);
const allMemoryItems = computed(() => timeline.filter((item) => item.type === "mention" || item.type === "note"));

const handleEdit = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_EDIT_BUTTON_CLICKED);
    router.visit(connection_edit_url);
};

const isConfirmDeletePersonDialogOpen = ref(false);
const { execute: deleteConnection, onFetchResponse } = useFetch(`/api/connections/${connection.id}/`, { immediate: false }).delete();
onFetchResponse(() => {
    router.visit(connections_page_url);
});
const handleDeletePerson = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_DELETE, { source: "connection_detail" });
    deleteConnection();
};

const isConfirmUnlinkProfileDialogOpen = ref(false);
const {
    execute: executeUnlinkProfile,
    error: unlinkProfileError,
    isFetching: isUnlinkingProfile,
} = useFetch(`/api/connections/${connection.id}/`, { immediate: false }).put({ public_profile: null }).json();
const showUnlinkProfileError = computed(() => !!unlinkProfileError.value && !isSessionEndedError(unlinkProfileError.value));
const handleUnlinkProfile = async () => {
    try {
        logUserInteraction(AmplitudeEventNames.CONNECTION_UNLINK_PUBLIC_PROFILE, {
            connection_id: connection.id,
            source: "my_people",
        });

        await executeUnlinkProfile();

        if (unlinkProfileError.value) {
            logError("Failed to unlink collaboration profile", unlinkProfileError.value);
        } else {
            isConfirmUnlinkProfileDialogOpen.value = false;
            router.reload({ only: ["connection", "public_profile_user_id", "public_profile_basic_info", "pair_perspective_url", "pair_perspective_page_url", "person_teams_url"] });
        }
    } catch (e) {
        logError(e);
    }
};

const handleMerged = ({ keptId }: { keptId: string }) => {
    // If the record being viewed survived the merge, refresh it; otherwise the
    // current connection was removed, so navigate to the surviving record.
    if (keptId === connection.id) {
        router.reload();
    } else {
        router.visit(`/people/${keptId}/`);
    }
};

const handleNoteAdd = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_ADD_CONTEXT_BUTTON_CLICKED);
    router.get(`/people/${connection.id}/note/`, {
        preserveState: true,
    });
};

const handleLinkProfile = () => {
    logUserInteraction(AmplitudeEventNames.CONNECTION_LINK_PROFILE_BUTTON_CLICKED, {
        connection_id: connection.id,
        source: "my_people",
    });
    focusLinkProfileSearch.value = true;
    showLinkProfileSearch.value = true;
};

const handleDismissProfileSearch = async () => {
    showLinkProfileSearch.value = false;
    focusLinkProfileSearch.value = false;
    await nextTick();
    linkProfileButton.value?.$el?.focus();
};

const handleTextSend = async ({ content, source = "text", mentions = [] }: { content: string; source?: string; mentions?: ComposerMention[] }) => {
    if (isSending.value) {
        return;
    }
    isSending.value = true;
    error.value = null;

    // Files in the store are a mix of UploadedFile (from /api/chat/document/upload)
    // and pasted-content temp objects (from ChatMessageControls.handlePaste).
    // The backend FileModel union accepts both shapes verbatim.
    const files = useChatStore.files.filter((f) => "id" in f || "content" in f);

    try {
        const redirectUrl = await startChat(connection, {
            message: content,
            mentions,
            files,
            logSource: source,
        });

        if (redirectUrl) {
            composerDraft.value.text = null;
            composerDraft.value.files = [];
            useChatStore.removeAllFiles();
            router.visit(redirectUrl);
        } else {
            throw new Error("No redirect URL received");
        }
    } catch (e) {
        error.value = t("connections.detail.startChatError");
        logError(`Failed to start chat: ${JSON.stringify(e)}`);
    } finally {
        isSending.value = false;
    }
};

const handleRecordingComplete = async ({ blob, audioStats }: { blob: Blob; audioStats?: AudioStats }) => {
    const transcript = await transcribe({ blob, audioStats });
    if (transcript) {
        handleTextSend({ content: transcript, source: "voice" });
    }
};

watch(
    () => public_profile_user_id,
    (newValue) => {
        showLinkProfileSearch.value = !newValue;
        focusLinkProfileSearch.value = false;
    },
);

onMounted(() => {
    globalProperties?.$setEventContext(event_context);
    isReady.value = true;
});

onUnmounted(() => {
    globalProperties?.$setEventContext({});
});
</script>

<style scoped>
@reference "../../../css/tailwind.css";
</style>
